import type { Emulator, GbaButton } from '../emulator';
import { haptic } from '../telegram';
import { isRotated } from './orientation';

const DPAD_DEADZONE = 0.18; // fração do raio do direcional

/**
 * Controles de toque com multitoque. Cada dedo pode deslizar entre botões
 * (ex.: de B para A) e girar no direcional sem levantar.
 */
export function mountControls(layer: HTMLElement, emulator: Emulator): void {
  const heldByPointer = new Map<number, Set<GbaButton>>();
  let pressed = new Set<GbaButton>();

  const dpadDirection = (dpad: HTMLElement, x: number, y: number): GbaButton[] => {
    const rect = dpad.getBoundingClientRect();
    const sx = (x - (rect.left + rect.width / 2)) / (rect.width / 2);
    const sy = (y - (rect.top + rect.height / 2)) / (rect.height / 2);
    // Com o jogo girado 90° pelo app, o "direita" do direcional aponta para baixo na tela.
    const [dx, dy] = isRotated() ? [sy, -sx] : [sx, sy];
    if (Math.hypot(dx, dy) < DPAD_DEADZONE) return [];
    // Pokémon anda só em 4 direções; diagonal confunde o jogo.
    if (Math.abs(dx) > Math.abs(dy)) return [dx > 0 ? 'RIGHT' : 'LEFT'];
    return [dy > 0 ? 'DOWN' : 'UP'];
  };

  const buttonsAt = (x: number, y: number): GbaButton[] => {
    const target = document.elementFromPoint(x, y)?.closest<HTMLElement>('[data-btn], [data-dpad]');
    if (!target || !layer.contains(target)) return [];
    if (target.dataset.dpad !== undefined) return dpadDirection(target, x, y);
    return [target.dataset.btn as GbaButton];
  };

  const sync = () => {
    const next = new Set<GbaButton>();
    for (const held of heldByPointer.values()) held.forEach((b) => next.add(b));

    for (const button of pressed) {
      if (!next.has(button)) {
        emulator.release(button);
        setVisualState(button, false);
      }
    }
    let newPress = false;
    for (const button of next) {
      if (!pressed.has(button)) {
        emulator.press(button);
        setVisualState(button, true);
        newPress = true;
      }
    }
    if (newPress) haptic();
    pressed = next;
  };

  const setVisualState = (button: GbaButton, isPressed: boolean) => {
    layer
      .querySelectorAll<HTMLElement>(`[data-btn="${button}"], [data-dir="${button}"]`)
      .forEach((el) => el.classList.toggle('is-pressed', isPressed));
  };

  const track = (event: PointerEvent) => {
    heldByPointer.set(event.pointerId, new Set(buttonsAt(event.clientX, event.clientY)));
    sync();
  };

  layer.addEventListener('pointerdown', (event) => {
    if (!(event.target as HTMLElement).closest('[data-btn], [data-dpad]')) return;
    event.preventDefault();
    layer.setPointerCapture(event.pointerId);
    void emulator.resumeAudio();
    track(event);
  });
  layer.addEventListener('pointermove', (event) => {
    if (heldByPointer.has(event.pointerId)) track(event);
  });
  const end = (event: PointerEvent) => {
    if (!heldByPointer.delete(event.pointerId)) return;
    sync();
  };
  layer.addEventListener('pointerup', end);
  layer.addEventListener('pointercancel', end);
  layer.addEventListener('contextmenu', (event) => event.preventDefault());

  // Se a Mini App perder o foco com um botão apertado, solta tudo.
  window.addEventListener('blur', () => {
    heldByPointer.clear();
    sync();
  });
}
